import React, { type JSX } from 'react';

import { Icon } from '@iconify/react';
import { TFunction } from 'i18next';

import PlusImg from '~/assets/images/plus.webp';
import { PRODUCTS_PROJECT_CONFIG } from '~/constants/products';
import extension from '~/services/Extension';
import { LimitsRenewPeriod } from '~/types/common';
import { REGULARITY } from '~/types/product';

import { ReactComponent as ContentDetector } from './content_detector.svg';
import styles from './styles.module.scss';

import { OrderDescriptionType } from './index';

const getListItemValuesByPeriod = (
  t: TFunction,
): Record<LimitsRenewPeriod, string> => ({
  [LimitsRenewPeriod.WEEK]: t(
    'subscription:payment_form.order_details_onetime_detectors[1]',
    {
      context: 'week',
      defaultValue: 'It will not affect the weekly limit',
    },
  ),
  [LimitsRenewPeriod.MONTH]: t(
    'subscription:payment_form.order_details_onetime_detectors[1]',
    {
      context: 'month',
      defaultValue: 'It will not affect the monthly limit',
    },
  ),
});

const getOnetimeDetectorsDescriptions = (
  wordCount: number,
  renewPeriod: LimitsRenewPeriod,
  t: TFunction,
): {
  details_list_ui: JSX.Element[];
  image_src_ui: string;
} => {
  return {
    details_list_ui: [
      <>
        <Icon icon="foundation:check" className={styles.detail__icon} />
        <span>
          {wordCount.toLocaleString()} {t('general:words')}
        </span>
      </>,
      <>
        <Icon icon="foundation:check" className={styles.detail__icon} />
        <span>
          {t(
            'subscription:payment_form.order_details_onetime_detectors[0]',
            'Extra words is a one-time purchase',
          )}
        </span>
      </>,
      <>
        <Icon icon="foundation:check" className={styles.detail__icon} />
        <span>{getListItemValuesByPeriod(t)[renewPeriod]}</span>
      </>,
    ],
    image_src_ui: PlusImg,
  };
};

// We don't use stripe id's since we have a default(not custom) stripe checkout
export const getExtraProductDescriptions = (
  productId: string,
  productRegularity: REGULARITY,
  renewPeriod: LimitsRenewPeriod,
  t: TFunction,
): OrderDescriptionType => {
  if (productRegularity === REGULARITY.YEAR) {
    return {
      details_list_ui: [
        <>
          <Icon icon="foundation:check" className={styles.detail__icon} />
          <span>
            {t(
              'subscription:payment_form.order_details_yearly[0]',
              'Unlimited words in AI Text Humanizer, AI Content Detector, Plagiarism Detector',
            )}
          </span>
        </>,
        <>
          <Icon icon="foundation:check" className={styles.detail__icon} />
          <span>
            {t(
              'subscription:payment_form.order_details_yearly[1]',
              'Unlimited words in AI Chat',
            )}
          </span>
        </>,
        <>
          <Icon icon="foundation:check" className={styles.detail__icon} />
          <span>
            {t(
              'subscription:payment_form.order_details_yearly[2]',
              'Unlimited words in other tools',
            )}
          </span>
        </>,
        <>
          <Icon icon="foundation:check" className={styles.detail__icon} />
          <span>
            {t('subscription:payment_form.order_details_yearly[3]', {
              title: extension.getTitle(),
              defaultValue: 'Unlimited {{title}} Extension checks',
            })}
          </span>
        </>,
        <>
          <Icon icon="foundation:check" className={styles.detail__icon} />
          <span>
            {t(
              'subscription:payment_form.order_details_yearly[4]',
              'Unlimited Image generations',
            )}
          </span>
        </>,
        <>
          <Icon icon="foundation:check" className={styles.detail__icon} />
          <span>
            {t(
              'subscription:payment_form.order_details_yearly[5]',
              'Unlimited prompt improvements',
            )}
          </span>
        </>,
      ],
      image_src_ui: '',
    };
  }

  switch (productId) {
    case PRODUCTS_PROJECT_CONFIG.ONETIME_DETECTORS_10_DOLLARS_10K_WORDS_ID:
      return getOnetimeDetectorsDescriptions(10000, renewPeriod, t);

    case PRODUCTS_PROJECT_CONFIG.ONETIME_DETECTORS_20_DOLLARS_20K_WORDS_ID:
      return getOnetimeDetectorsDescriptions(20000, renewPeriod, t);

    case PRODUCTS_PROJECT_CONFIG.ONETIME_DETECTORS_40_DOLLARS_50K_WORDS_ID:
      return getOnetimeDetectorsDescriptions(50000, renewPeriod, t);

    case PRODUCTS_PROJECT_CONFIG.ONETIME_DETECTORS_80_DOLLARS_100K_WORDS_ID:
      return getOnetimeDetectorsDescriptions(100000, renewPeriod, t);

    case PRODUCTS_PROJECT_CONFIG?.UNBLOCK_DETECTOR_TOOLS_10_DOLLARS_ID:
      return {
        details_list_ui: [
          <>
            <Icon
              icon="material-symbols:quick-reference-all-outline-rounded"
              className={styles.detail__paraphrase_icon}
            />
            <span>
              {t(
                'subscription:payment_form.order_details_unblock_detectors[0]',
                'Plagiarism Level Scanner',
              )}
            </span>
          </>,
          <>
            <ContentDetector />
            <span>
              {t(
                'subscription:payment_form.order_details_unblock_detectors[1]',
                'AI Content Detector',
              )}
            </span>
          </>,
          <>
            <Icon
              icon="material-symbols:frame-person"
              className={styles.detail__paraphrase_icon}
            />
            <span>
              {t(
                'subscription:payment_form.order_details_unblock_detectors[2]',
                'AI Text Humanizer',
              )}
            </span>
          </>,
        ],
        image_src_ui: PlusImg,
      };

    default:
      return {
        details_list_ui: [
          <>
            <Icon icon="foundation:check" className={styles.detail__icon} />
            <span>
              {t('subscription:payment_form.order_details_default[0]_v2', {
                tools_amount: 25,
                defaultValue:
                  'Unlimited Words for {{tools_amount}}+ Advanced AI Tools',
              })}
            </span>
          </>,
          <>
            <Icon icon="foundation:check" className={styles.detail__icon} />
            <span>
              {t(
                'subscription:payment_form.order_details_default[1]',
                '2000+ word long Article Generation Tool',
              )}
            </span>
          </>,
          <>
            <Icon icon="foundation:check" className={styles.detail__icon} />
            <span>
              {t(
                'subscription:payment_form.order_details_default[2]',
                'Plagiarism Checker and AI Detector',
              )}
            </span>
          </>,
          <>
            <Icon icon="foundation:check" className={styles.detail__icon} />
            <span>
              {t('subscription:payment_form.order_details_default[3]', {
                title: extension.getTitle(),
                defaultValue: 'Ultimate {{title}} Extension',
              })}
            </span>
          </>,
        ],
        image_src_ui: '',
      };
  }
};
